import { computed, makeObservable, observable } from 'mobx';
import type { Aggregate } from 'edit/Aggregate';
import type { OutputMatcher, OutputMatcherFile, OutputMatcherMatchType, OutputMatcherProceed } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

/**
 * How a matched pattern supplies `new_exit_code` on the wire. The wire has no Success/Fail enum — only a nullable integer
 * that the backend maps to status (`0` → completed, non-zero → failed, `null` → leave the agent's exit/status alone). The UI
 * splits that into Success (always exit 0, field disabled and showing 0) vs Fail (editable: empty or any non-zero integer —
 * `0` is not a Fail value because the backend treats it as completed).
 */
export type OverrideStatus = 'success' | 'fail';

const DEFAULT_OUTPUT_FILE: OutputMatcherFile = 'stdout.log';
const DEFAULT_OVERRIDE_STATUS: OverrideStatus = 'fail';
const DEFAULT_PROCEED: OutputMatcherProceed = 'continue';
const DEFAULT_MATCH_TYPE: OutputMatcherMatchType = 'file';

// Monotonic source of stable per-instance keys for React list rendering / drag ids. New matchers have no server id, and array
// index is unstable across add/remove/reorder, so each instance gets a unique key at construction that never changes.
let nextOutputMatcherKey = 0;

/**
 * One output matcher, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is owned by a shared
 * `Aggregate` that both sides hold; this child routes its self-delete through it.
 *
 * User-fillable fields with no logic are plain public observables — read `matcher.outputFile`, write `matcher.outputFile = x`.
 * A field only earns an accessor pair when there's behaviour to hide: the Fail return code is kept even while Success is
 * selected (the form shows 0 and disables the field, without clearing the Fail code) so toggling Success↔Fail doesn't lose a
 * typed code, and entering `0` in Fail mode switches to Success to match backend status mapping. `toSave` reads only what the
 * chosen override implies. `match_type` is mutually exclusive: file-content fields vs `returnCodes`.
 */
export class EditableOutputMatcher {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableOutputMatcher>;
  caseInsensitive = false;
  /** Stable identity for React list keys / drag ids; never changes after construction. */
  readonly key = `output-matcher-${(nextOutputMatcherKey += 1)}`;
  matchType: OutputMatcherMatchType = DEFAULT_MATCH_TYPE;
  outputFile: OutputMatcherFile = DEFAULT_OUTPUT_FILE;
  /** Return code shown/edited for Fail (`null` or non-zero). Not used while Success is selected (UI shows 0). */
  private _overrideReturnCode: number | null = null;
  overrideStatus: OverrideStatus = DEFAULT_OVERRIDE_STATUS;
  proceed: OutputMatcherProceed = DEFAULT_PROCEED;
  returnCodes: readonly number[] = [];
  searchString: string | null = null;
  useRegex = false;

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds the fields from a loaded matcher; when absent, the field defaults stand (a fresh Fail
   *   matcher with an empty return code = keep agent exit/status).
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableOutputMatcher>, wire?: OutputMatcher) {
    this._aggregate = aggregate;
    makeObservable<EditableOutputMatcher, '_overrideReturnCode'>(this, {
      caseInsensitive: observable,
      matchType: observable,
      outputFile: observable,
      _overrideReturnCode: observable,
      overrideStatus: observable,
      proceed: observable,
      returnCodes: observable.ref,
      searchString: observable,
      useRegex: observable,
      summary: computed,
    });
    if (wire !== undefined) {
      this.matchType = wire.match_type;
      // Only the loaded branch's source fields are seeded; the other keeps its defaults, so switching match type in the
      // form starts that side clean rather than showing values the stored matcher never had.
      if (wire.match_type === 'return_code') {
        this.returnCodes = wire.return_codes;
      } else {
        this.outputFile = wire.output_file ?? DEFAULT_OUTPUT_FILE;
        this.searchString = wire.search_string ?? null;
        this.caseInsensitive = wire.case_insensitive ?? false;
        this.useRegex = wire.use_regex ?? false;
      }
      this.proceed = wire.proceed ?? DEFAULT_PROCEED;
      if (wire.new_exit_code === 0) {
        this.overrideStatus = 'success';
      } else {
        // Non-zero or null -> Fail mode. Null means keep the agent's exit/status on match.
        this.overrideStatus = 'fail';
        this._overrideReturnCode = wire.new_exit_code ?? null;
      }
    }
  }

  /**
   * Value shown in the return-code field. Success always displays 0 (field is disabled); Fail shows the typed code
   * (`null` or non-zero — never 0 in Fail mode).
   */
  get overrideReturnCode(): number | null {
    return this.overrideStatus === 'success' ? 0 : this._overrideReturnCode;
  }

  /**
   * Empty (`null`) and any non-zero integer are kept in Fail mode. `0` switches to Success because the backend maps exit 0
   * to completed. Callers just assign `matcher.overrideReturnCode = x`; the Fail/Success bookkeeping stays internal.
   */
  set overrideReturnCode(value: number | null) {
    if (value === 0) {
      this.overrideStatus = 'success';
      return;
    }
    this._overrideReturnCode = value;
  }

  /**
   * A one-line plain-language summary of the matcher, or `null` when the match source is incomplete. Presentational — the
   * tab renders it as the card subtitle.
   */
  get summary(): string | null {
    const code = this._overrideReturnCode;
    const outcome = this.overrideStatus === 'fail' ? (code !== null ? `Fail (${code})` : 'Fail') : 'Success';
    if (this.matchType === 'return_code') {
      if (this.returnCodes.length === 0) {
        return null;
      }
      return `${outcome} when return code is ${this.returnCodes.join(', ')}`;
    }
    if (!this.searchString) {
      return null;
    }
    return `${outcome} when ${this.outputFile} contains "${this.searchString}"`;
  }

  /** Remove this matcher from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape. Success -> exit 0; Fail -> the typed return code (`null` keeps agent exit/status; non-zero
   * only — Fail never persists 0). `sort_order` is omitted — array position conveys order. File and return-code sources are
   * mutually exclusive on the wire.
   */
  toSave(): OutputMatcher {
    const newExitCode = this.overrideReturnCode;

    if (this.matchType === 'return_code') {
      return {
        match_type: 'return_code',
        return_codes: this.returnCodes,
        new_exit_code: newExitCode,
        proceed: this.proceed,
      };
    }

    return {
      match_type: 'file',
      output_file: this.outputFile,
      search_string: this.searchString ?? '',
      case_insensitive: this.caseInsensitive,
      use_regex: this.useRegex,
      new_exit_code: newExitCode,
      proceed: this.proceed,
    };
  }
}
